import {
    createLootSankeyProjector,
    createLootSankeyProjectorCache,
} from "./components/loot-sankey-projection.js";
import {
    CALCULATOR_SANKEY_METRICS,
    createCalculatorFormulaPresenter,
} from "./calculator-formula-presenter.js";
import {
    createCalculatorLootProjectionValidator,
} from "./components/calculator-loot-projection-contract.js";
import {
    createCalculatorStatTermSubjectParser,
} from "./components/calculator-stat-term-subject-contract.js";
import {
    calculatorLootRateDisplay,
    calculatorLootRateDisplayForMode,
    parseCalculatorLootRateDisplayMode,
} from "./components/calculator-loot-rate-presentation.js";
import {
    calculatorMetricTextWithUnit,
    calculatorMetricUnitMarkForValueField,
} from "./components/calculator-metric-unit-mark.js";
import {
    parseCalculatorSourceMainGroupPresentation,
} from "./components/calculator-presentation-contract.js";

(function registerCalculatorProjectionPresenter(global) {
    "use strict";

    const API_NAME = "FishystuffCalculatorProjectionPresenter";
    const PROJECTION_FIELDS = Object.freeze([
        "engineVersion",
        "modelRevision",
        "userLayerContentHash",
        "normalizedScenario",
        "resourceAbundance",
        "presentationOrder",
        "provenanceTraces",
        "rateAlternativeResolutions",
        "sankey",
        "groupRateAuthoring",
        "schemaVersion",
        "statBreakdowns",
        "summaries",
        "target",
        "timeline",
        "unquantifiedItems",
    ]);
    const STATIC_FIELDS = Object.freeze([
        "distributions",
        "formulas",
        "resources",
        "sankey",
        "schemaVersion",
        "session",
        "statTerms",
        "stats",
        "summaryMetrics",
        "target",
        "timeline",
        "values",
    ]);
    const VALUE_STATES = new Set(["known", "unknown", "unavailable"]);
    const STAT_TERM_ROLES = Object.freeze(["input", "modifier", "result"]);
    const STAT_TERM_ROLE_SET = new Set(STAT_TERM_ROLES);
    const STAT_TERM_RELATIONSHIPS = Object.freeze([
        "direct",
        "lookup",
        "additive",
        "multiplicative",
        "maximum",
        "result",
    ]);
    const STAT_TERM_RELATIONSHIP_SET = new Set(STAT_TERM_RELATIONSHIPS);
    const OPTION_KINDS = Object.freeze([
        "rod",
        "float",
        "chair",
        "lightstoneSet",
        "backpack",
        "outfit",
        "food",
        "buff",
        "pet",
        "petOption",
        "tradeOrigin",
        "tradeDestination",
    ]);
    const OPTION_KIND_SET = new Set(OPTION_KINDS);
    const PET_OPTION_KINDS = Object.freeze(["special", "talent", "learnedSkill"]);
    const GRADE_TONES = new Set(["red", "yellow", "blue", "green", "white", "unknown"]);
    const RESOURCE_ABUNDANCE_STATES = Object.freeze([
        "exhausted",
        "low",
        "average",
        "abundant",
    ]);
    const RESOURCE_ABUNDANCE_STATE_SET = new Set(RESOURCE_ABUNDANCE_STATES);
    const TIMESPAN_UNITS = Object.freeze(["minutes", "hours", "days", "weeks"]);
    const TIMESPAN_UNIT_SET = new Set(TIMESPAN_UNITS);
    const SESSION_TITLE_PRESENTATIONS = Object.freeze([
        "casts",
        "durabilityLoss",
        "targetExpected",
    ]);
    const RATE_BASES = new Set([
        "ratio",
        "percent",
        "denormalizedWeight",
        "rawWeight",
        "perCatch",
        "perHour",
    ]);
    const DISTRIBUTION_DEFINITIONS = Object.freeze({
        "group-count": Object.freeze({
            field: "expectedCount",
            summaryMetric: "totalCatches",
            unit: "count",
        }),
        "group-profit": Object.freeze({
            field: "expectedProfit",
            summaryMetric: "totalProfit",
            unit: "silver",
        }),
        "group-fishing-experience": Object.freeze({
            field: "expectedFishingExperience",
            summaryMetric: "fishingExperience",
            unit: "experience",
        }),
        "group-totem-experience": Object.freeze({
            field: "expectedTotemExperience",
            summaryMetric: "totemExperience",
            unit: "experience",
        }),
    });
    const DISTRIBUTION_METRICS = Object.freeze(
        Object.values(DISTRIBUTION_DEFINITIONS).map(({ field }) => field),
    );
    const SANKEY_METRIC_DEFINITIONS = Object.freeze(Object.fromEntries(
        Object.values(DISTRIBUTION_DEFINITIONS).map((definition) => [
            definition.field,
            Object.freeze({
                summaryMetric: definition.summaryMetric,
                unit: definition.unit,
            }),
        ]),
    ));
    const STAT_PRESENTATION_PROPERTY = "calculatorStatPresentation";
    const SANKEY_VARIANTS = Object.freeze({
        "loot-sankey:profit": "profit",
        "loot-sankey:fishing-experience": "fishingExperience",
        "loot-sankey:totem-experience": "totemExperience",
    });

    class ContractError extends TypeError {
        constructor(path, message) {
            super(`calculator projection presenter contract failed at ${path}: ${message}`);
            this.name = "CalculatorProjectionPresenterContractError";
            this.path = path;
        }
    }

    function requireRecord(value, path) {
        if (value === null || typeof value !== "object" || Array.isArray(value)) {
            throw new ContractError(path, "expected an object");
        }
        return value;
    }

    function requireExactFields(value, fields, path) {
        const record = requireRecord(value, path);
        const expected = [...fields].sort();
        const actual = Object.keys(record).sort();
        if (
            actual.length !== expected.length
            || actual.some((field, index) => field !== expected[index])
        ) {
            throw new ContractError(path, `fields must be exactly: ${expected.join(", ")}`);
        }
        return record;
    }

    function requireArray(value, path) {
        if (!Array.isArray(value)) {
            throw new ContractError(path, "expected an array");
        }
        return value;
    }

    function requireString(value, path, { allowEmpty = false } = {}) {
        if (typeof value !== "string" || (!allowEmpty && value.length === 0)) {
            throw new ContractError(
                path,
                allowEmpty ? "expected a string" : "expected a non-empty string",
            );
        }
        return value;
    }

    function requireNullableString(value, path) {
        return value === null ? null : requireString(value, path);
    }

    function validateRateAlternativeResolutions(value, path) {
        const families = new Set();
        requireArray(value, path).forEach((entry, index) => {
            const entryPath = `${path}[${index}]`;
            const record = requireRecord(entry, entryPath);
            const kind = requireString(record.kind, `${entryPath}.kind`);
            const fields = kind === "automatic" || kind === "override"
                ? ["kind", "familyId", "alternativeId"]
                : kind === "selectionRequired" || kind === "inactive"
                    ? ["kind", "familyId"]
                    : null;
            if (fields === null) {
                throw new ContractError(
                    `${entryPath}.kind`,
                    "expected automatic, override, selectionRequired, or inactive",
                );
            }
            const resolution = requireExactFields(record, fields, entryPath);
            const familyId = requireString(
                resolution.familyId,
                `${entryPath}.familyId`,
            );
            if (families.has(familyId)) {
                throw new ContractError(entryPath, "duplicate rate alternative family");
            }
            families.add(familyId);
            if (Object.hasOwn(resolution, "alternativeId")) {
                requireString(resolution.alternativeId, `${entryPath}.alternativeId`);
            }
        });
    }

    function requireFinite(value, path, { minimum = -Infinity, maximum = Infinity } = {}) {
        if (!Number.isFinite(value) || value < minimum || value > maximum) {
            throw new ContractError(path, "expected a finite number in range");
        }
        return value;
    }

    function requireInteger(value, path, { minimum = 0 } = {}) {
        if (!Number.isInteger(value) || value < minimum) {
            throw new ContractError(path, `expected an integer of at least ${minimum}`);
        }
        return value;
    }

    function requireBoolean(value, path) {
        if (typeof value !== "boolean") {
            throw new ContractError(path, "expected a boolean");
        }
        return value;
    }

    const parseStatTermSubject = createCalculatorStatTermSubjectParser({
        fail(path, message) {
            throw new ContractError(path, message);
        },
        requireBoolean,
        requireExactFields,
        requireFinite,
        requireInteger,
        requireRecord,
        requireString,
    });

    const formulaPresenter = createCalculatorFormulaPresenter({
        ContractError,
        applyPattern: applyTermPattern,
        formatNumeric,
        parseNumericValue,
        parseRate,
        requireArray,
        requireExactFields,
        requireFinite,
        requireInteger,
        requireRecord,
        requireString,
    });

    function parseFormat(value, path, locale) {
        const format = requireExactFields(
            value,
            [
                "maximumFractionDigits",
                "minimumFractionDigits",
                "multiplier",
                "prefix",
                "suffix",
            ],
            path,
        );
        const minimumFractionDigits = requireInteger(
            format.minimumFractionDigits,
            `${path}.minimumFractionDigits`,
        );
        const maximumFractionDigits = requireInteger(
            format.maximumFractionDigits,
            `${path}.maximumFractionDigits`,
        );
        if (maximumFractionDigits > 20 || minimumFractionDigits > maximumFractionDigits) {
            throw new ContractError(path, "fraction digits must be ordered and no greater than 20");
        }
        const parsed = {
            minimumFractionDigits,
            maximumFractionDigits,
            multiplier: requireFinite(format.multiplier, `${path}.multiplier`, { minimum: 0 }),
            prefix: requireString(format.prefix, `${path}.prefix`, { allowEmpty: true }),
            suffix: requireString(format.suffix, `${path}.suffix`, { allowEmpty: true }),
        };
        parsed.formatter = new Intl.NumberFormat(locale, {
            minimumFractionDigits,
            maximumFractionDigits,
        });
        return parsed;
    }

    function parseFormatTable(value, path, locale) {
        const table = requireRecord(value, path);
        return Object.fromEntries(Object.entries(table).map(([key, entry]) => [
            requireString(key, `${path} key`),
            parseFormat(entry, `${path}.${key}`, locale),
        ]));
    }

    function parseBreakdownMeta(value, path, { includeInputs }) {
        const fields = ["color", "eyebrow", "resultLabel", "resultSectionLabel", "title"];
        if (includeInputs) {
            fields.push("inputSectionLabel");
        } else {
            fields.push("statKind");
        }
        const meta = requireExactFields(value, fields, path);
        const parsed = {
            color: requireString(meta.color, `${path}.color`),
            eyebrow: requireString(meta.eyebrow, `${path}.eyebrow`),
            resultLabel: requireString(meta.resultLabel, `${path}.resultLabel`),
            resultSectionLabel: requireString(
                meta.resultSectionLabel,
                `${path}.resultSectionLabel`,
            ),
            title: requireString(meta.title, `${path}.title`),
        };
        if (includeInputs) {
            parsed.inputSectionLabel = requireString(
                meta.inputSectionLabel,
                `${path}.inputSectionLabel`,
            );
        } else {
            parsed.statKind = requireNullableString(meta.statKind, `${path}.statKind`);
        }
        return parsed;
    }

    function parseBreakdownMetaTable(value, path, options) {
        return Object.fromEntries(Object.entries(requireRecord(value, path)).map(([key, meta]) => [
            requireString(key, `${path} key`),
            parseBreakdownMeta(meta, `${path}.${key}`, options),
        ]));
    }

    function parseTimelineMeta(value) {
        const timeline = requireExactFields(value, ["segments"], "$.presentation.timeline");
        return {
            segments: Object.fromEntries(Object.entries(requireRecord(
                timeline.segments,
                "$.presentation.timeline.segments",
            )).map(([kind, entry]) => {
                const path = `$.presentation.timeline.segments.${kind}`;
                const meta = requireExactFields(
                    entry,
                    ["fillColor", "label", "strokeColor", "summaryMetric"],
                    path,
                );
                return [kind, {
                    label: requireString(meta.label, `${path}.label`),
                    fillColor: requireString(meta.fillColor, `${path}.fillColor`),
                    strokeColor: requireString(meta.strokeColor, `${path}.strokeColor`),
                    summaryMetric: requireString(meta.summaryMetric, `${path}.summaryMetric`),
                }];
            })),
        };
    }

    function parseTargetMeta(value, locale) {
        const path = "$.presentation.target";
        const target = requireExactFields(
            value,
            ["bucketLabels", "durationFormats", "expectedValuePattern"],
            path,
        );
        const labels = requireExactFields(
            target.bucketLabels,
            ["atLeast", "exact", "inclusiveRange"],
            `${path}.bucketLabels`,
        );
        const durationFormats = requireArray(
            target.durationFormats,
            `${path}.durationFormats`,
        ).map((entry, index) => {
            const entryPath = `${path}.durationFormats[${index}]`;
            const format = requireExactFields(
                entry,
                ["minimumSeconds", "numberFormat", "unitLabels"],
                entryPath,
            );
            const minimumSeconds = requireFinite(
                format.minimumSeconds,
                `${entryPath}.minimumSeconds`,
                { minimum: 0 },
            );
            const numberFormat = parseFormat(
                format.numberFormat,
                `${entryPath}.numberFormat`,
                locale,
            );
            if (numberFormat.multiplier <= 0) {
                throw new ContractError(
                    `${entryPath}.numberFormat.multiplier`,
                    "expected a positive duration multiplier",
                );
            }
            const unitLabels = requireExactFields(
                format.unitLabels,
                ["one", "other"],
                `${entryPath}.unitLabels`,
            );
            return {
                minimumSeconds,
                numberFormat,
                unitLabels: {
                    one: requireString(unitLabels.one, `${entryPath}.unitLabels.one`),
                    other: requireString(unitLabels.other, `${entryPath}.unitLabels.other`),
                },
            };
        });
        if (durationFormats.length === 0) {
            throw new ContractError(`${path}.durationFormats`, "expected at least one format");
        }
        for (let index = 1; index < durationFormats.length; index += 1) {
            if (
                durationFormats[index - 1].minimumSeconds
                <= durationFormats[index].minimumSeconds
            ) {
                throw new ContractError(
                    `${path}.durationFormats[${index}].minimumSeconds`,
                    "duration thresholds must be strictly descending",
                );
            }
        }
        if (durationFormats.at(-1).minimumSeconds !== 0) {
            throw new ContractError(
                `${path}.durationFormats`,
                "the final duration threshold must be zero",
            );
        }
        return {
            expectedValuePattern: requireString(
                target.expectedValuePattern,
                `${path}.expectedValuePattern`,
            ),
            durationFormats,
            bucketLabels: {
                exact: requireString(labels.exact, `${path}.bucketLabels.exact`),
                inclusiveRange: requireString(
                    labels.inclusiveRange,
                    `${path}.bucketLabels.inclusiveRange`,
                ),
                atLeast: requireString(labels.atLeast, `${path}.bucketLabels.atLeast`),
            },
        };
    }

    function parseDistributionMeta(value, index) {
        const path = `$.presentation.distributions[${index}]`;
        const distribution = requireExactFields(
            value,
            ["groupsByGroupId", "id", "valueField"],
            path,
        );
        const id = requireString(distribution.id, `${path}.id`);
        if (!Object.prototype.hasOwnProperty.call(DISTRIBUTION_DEFINITIONS, id)) {
            throw new ContractError(`${path}.id`, "unsupported distribution ID");
        }
        const definition = DISTRIBUTION_DEFINITIONS[id];
        if (distribution.valueField !== definition.field) {
            throw new ContractError(
                `${path}.valueField`,
                `expected ${definition.field} for ${id}`,
            );
        }
        const groupsByGroupId = Object.fromEntries(Object.entries(requireRecord(
            distribution.groupsByGroupId,
            `${path}.groupsByGroupId`,
        )).map(([groupId, entry]) => {
            const groupPath = `${path}.groupsByGroupId.${groupId}`;
            const meta = requireExactFields(
                entry,
                ["connectorColor", "fillColor", "label", "strokeColor", "textColor"],
                groupPath,
            );
            return [groupId, {
                label: requireString(meta.label, `${groupPath}.label`),
                fillColor: requireString(meta.fillColor, `${groupPath}.fillColor`),
                strokeColor: requireString(meta.strokeColor, `${groupPath}.strokeColor`),
                textColor: requireString(meta.textColor, `${groupPath}.textColor`),
                connectorColor: requireString(
                    meta.connectorColor,
                    `${groupPath}.connectorColor`,
                ),
            }];
        }));
        return {
            id,
            valueField: distribution.valueField,
            summaryMetric: definition.summaryMetric,
            valueUnit: definition.unit,
            groupsByGroupId,
        };
    }

    function parseResourceMeta(value) {
        const path = "$.presentation.resources";
        const resources = requireExactFields(
            value,
            ["abundanceLabels", "labelPattern"],
            path,
        );
        const labels = requireExactFields(
            resources.abundanceLabels,
            RESOURCE_ABUNDANCE_STATES,
            `${path}.abundanceLabels`,
        );
        return {
            labelPattern: requireString(resources.labelPattern, `${path}.labelPattern`),
            abundanceLabels: Object.fromEntries(RESOURCE_ABUNDANCE_STATES.map((state) => [
                state,
                requireString(labels[state], `${path}.abundanceLabels.${state}`),
            ])),
        };
    }

    function parseExactStringTable(value, keys, path) {
        const table = requireExactFields(value, keys, path);
        return Object.fromEntries(keys.map((key) => [
            key,
            requireString(table[key], `${path}.${key}`),
        ]));
    }

    function parseSessionMeta(value, locale) {
        const path = "$.presentation.session";
        const session = requireExactFields(value, ["timespan", "titlePatterns"], path);
        const timespanPath = `${path}.timespan`;
        const timespan = requireExactFields(
            session.timespan,
            ["numberFormat", "unitLabels"],
            timespanPath,
        );
        const unitLabels = requireExactFields(
            timespan.unitLabels,
            TIMESPAN_UNITS,
            `${timespanPath}.unitLabels`,
        );
        return {
            timespan: {
                numberFormat: parseFormat(
                    timespan.numberFormat,
                    `${timespanPath}.numberFormat`,
                    locale,
                ),
                unitLabels: Object.fromEntries(TIMESPAN_UNITS.map((unit) => [
                    unit,
                    parseExactStringTable(
                        unitLabels[unit],
                        ["one", "other"],
                        `${timespanPath}.unitLabels.${unit}`,
                    ),
                ])),
            },
            titlePatterns: parseExactStringTable(
                session.titlePatterns,
                SESSION_TITLE_PRESENTATIONS,
                `${path}.titlePatterns`,
            ),
        };
    }

    function parseStatIdentityTable(value, path, { option = false } = {}) {
        return Object.fromEntries(Object.entries(requireRecord(value, path)).map(([id, entry]) => {
            const entryPath = `${path}.${id}`;
            const fields = ["gradeTone", "iconUrl", "label"];
            if (option) {
                fields.push("optionKind");
            }
            const identity = requireExactFields(entry, fields, entryPath);
            const gradeTone = requireString(identity.gradeTone, `${entryPath}.gradeTone`);
            if (!GRADE_TONES.has(gradeTone)) {
                throw new ContractError(`${entryPath}.gradeTone`, "unsupported grade tone");
            }
            const parsed = {
                label: requireString(identity.label, `${entryPath}.label`),
                iconUrl: requireNullableString(identity.iconUrl, `${entryPath}.iconUrl`),
                gradeTone,
            };
            if (option) {
                parsed.optionKind = requireString(identity.optionKind, `${entryPath}.optionKind`);
                if (!OPTION_KIND_SET.has(parsed.optionKind)) {
                    throw new ContractError(`${entryPath}.optionKind`, "unsupported option kind");
                }
            }
            return [requireString(id, `${path} key`), parsed];
        }));
    }

    function parseSankeyStatIdentityTable(value) {
        const path = "$.presentation.sankey.itemsByItemId";
        return Object.fromEntries(Object.entries(requireRecord(value, path)).map(([id, entry]) => {
            const entryPath = `${path}.${id}`;
            const identity = requireExactFields(
                entry,
                [
                    "connectorColor",
                    "fillColor",
                    "gradeRank",
                    "iconGradeTone",
                    "iconUrl",
                    "label",
                    "strokeColor",
                    "textColor",
                ],
                entryPath,
            );
            const gradeTone = requireString(identity.iconGradeTone, `${entryPath}.iconGradeTone`);
            requireInteger(identity.gradeRank, `${entryPath}.gradeRank`);
            if (!GRADE_TONES.has(gradeTone)) {
                throw new ContractError(`${entryPath}.iconGradeTone`, "unsupported grade tone");
            }
            return [requireString(id, `${path} key`), {
                label: requireString(identity.label, `${entryPath}.label`),
                iconUrl: requireNullableString(identity.iconUrl, `${entryPath}.iconUrl`),
                gradeTone,
            }];
        }));
    }

    function parseStatTermsMeta(value) {
        const path = "$.presentation.statTerms";
        const statTerms = requireExactFields(
            value,
            ["kinds", "relationships", "roles", "subjects"],
            path,
        );
        const kinds = Object.fromEntries(Object.entries(requireRecord(
            statTerms.kinds,
            `${path}.kinds`,
        )).map(([kind, label]) => [
            requireString(kind, `${path}.kinds key`),
            requireString(label, `${path}.kinds.${kind}`),
        ]));
        const subjectsPath = `${path}.subjects`;
        const subjects = requireExactFields(
            statTerms.subjects,
            [
                "abundanceLabels",
                "brandActiveLabel",
                "brandInactiveLabel",
                "fishingLevelDetailPattern",
                "itemFallbackPattern",
                "lifeskillLevelDetailPattern",
                "masteryDetailPattern",
                "optionKinds",
                "optionsByOptionId",
                "packLeaderSuffix",
                "petDetailPattern",
                "petOptionKinds",
                "resourcesDetailPattern",
                "sourceLabel",
            ],
            subjectsPath,
        );
        return {
            kinds,
            roles: parseExactStringTable(
                statTerms.roles,
                STAT_TERM_ROLES,
                `${path}.roles`,
            ),
            relationships: parseExactStringTable(
                statTerms.relationships,
                STAT_TERM_RELATIONSHIPS,
                `${path}.relationships`,
            ),
            subjects: {
                optionsByOptionId: parseStatIdentityTable(
                    subjects.optionsByOptionId,
                    `${subjectsPath}.optionsByOptionId`,
                    { option: true },
                ),
                sourceLabel: requireString(subjects.sourceLabel, `${subjectsPath}.sourceLabel`),
                itemFallbackPattern: requireString(
                    subjects.itemFallbackPattern,
                    `${subjectsPath}.itemFallbackPattern`,
                ),
                petDetailPattern: requireString(
                    subjects.petDetailPattern,
                    `${subjectsPath}.petDetailPattern`,
                ),
                packLeaderSuffix: requireString(
                    subjects.packLeaderSuffix,
                    `${subjectsPath}.packLeaderSuffix`,
                    { allowEmpty: true },
                ),
                resourcesDetailPattern: requireString(
                    subjects.resourcesDetailPattern,
                    `${subjectsPath}.resourcesDetailPattern`,
                ),
                fishingLevelDetailPattern: requireString(
                    subjects.fishingLevelDetailPattern,
                    `${subjectsPath}.fishingLevelDetailPattern`,
                ),
                lifeskillLevelDetailPattern: requireString(
                    subjects.lifeskillLevelDetailPattern,
                    `${subjectsPath}.lifeskillLevelDetailPattern`,
                ),
                masteryDetailPattern: requireString(
                    subjects.masteryDetailPattern,
                    `${subjectsPath}.masteryDetailPattern`,
                ),
                brandActiveLabel: requireString(
                    subjects.brandActiveLabel,
                    `${subjectsPath}.brandActiveLabel`,
                ),
                brandInactiveLabel: requireString(
                    subjects.brandInactiveLabel,
                    `${subjectsPath}.brandInactiveLabel`,
                ),
                abundanceLabels: parseExactStringTable(
                    subjects.abundanceLabels,
                    RESOURCE_ABUNDANCE_STATES,
                    `${subjectsPath}.abundanceLabels`,
                ),
                optionKinds: parseExactStringTable(
                    subjects.optionKinds,
                    OPTION_KINDS,
                    `${subjectsPath}.optionKinds`,
                ),
                petOptionKinds: parseExactStringTable(
                    subjects.petOptionKinds,
                    PET_OPTION_KINDS,
                    `${subjectsPath}.petOptionKinds`,
                ),
            },
        };
    }

    function parseFormulaGroupIdentityTable(value) {
        const path = "$.presentation.sankey.groupsByGroupId";
        return Object.fromEntries(Object.entries(requireRecord(value, path)).map(([id, entry]) => {
            const entryPath = `${path}.${id}`;
            const identity = requireExactFields(
                entry,
                [
                    "connectorColor",
                    "fillColor",
                    "label",
                    "sourceMainGroup",
                    "strokeColor",
                    "textColor",
                ],
                entryPath,
            );
            parseCalculatorSourceMainGroupPresentation(
                identity.sourceMainGroup,
                `${entryPath}.sourceMainGroup`,
            );
            return [requireString(id, `${path} key`), {
                label: requireString(identity.label, `${entryPath}.label`),
            }];
        }));
    }

    function parseStaticPresentation(value, locale) {
        const presentation = requireExactFields(value, STATIC_FIELDS, "$.presentation");
        if (presentation.schemaVersion !== 1) {
            throw new ContractError("$.presentation.schemaVersion", "expected exactly 1");
        }
        const values = requireExactFields(
            presentation.values,
            ["numericUnits", "rates", "unavailableLabel", "unknownLabel"],
            "$.presentation.values",
        );
        const statTerms = parseStatTermsMeta(presentation.statTerms);
        const sankey = requireRecord(presentation.sankey, "$.presentation.sankey");
        const sankeyItemsByItemId = parseSankeyStatIdentityTable(
            sankey.itemsByItemId,
        );
        const sankeyGroupsByGroupId = parseFormulaGroupIdentityTable(
            sankey.groupsByGroupId,
        );
        const distributions = requireArray(
            presentation.distributions,
            "$.presentation.distributions",
        ).map(parseDistributionMeta);
        if (new Set(distributions.map(({ id }) => id)).size !== distributions.length) {
            throw new ContractError("$.presentation.distributions", "IDs must be unique");
        }
        const distributionIds = distributions.map(({ id }) => id).sort();
        const expectedDistributionIds = Object.keys(DISTRIBUTION_DEFINITIONS).sort();
        if (
            distributionIds.length !== expectedDistributionIds.length
            || distributionIds.some((id, index) => id !== expectedDistributionIds[index])
        ) {
            throw new ContractError(
                "$.presentation.distributions",
                `IDs must be exactly: ${expectedDistributionIds.join(", ")}`,
            );
        }
        return {
            locale,
            values: {
                unknownLabel: requireString(
                    values.unknownLabel,
                    "$.presentation.values.unknownLabel",
                ),
                unavailableLabel: requireString(
                    values.unavailableLabel,
                    "$.presentation.values.unavailableLabel",
                ),
                numericUnits: parseFormatTable(
                    values.numericUnits,
                    "$.presentation.values.numericUnits",
                    locale,
                ),
                rates: parseFormatTable(values.rates, "$.presentation.values.rates", locale),
            },
            summaryMetrics: parseBreakdownMetaTable(
                presentation.summaryMetrics,
                "$.presentation.summaryMetrics",
                { includeInputs: false },
            ),
            stats: parseBreakdownMetaTable(
                presentation.stats,
                "$.presentation.stats",
                { includeInputs: true },
            ),
            statTerms,
            formulas: formulaPresenter.parseMeta(presentation.formulas),
            sankeyItemsByItemId,
            sankeyGroupsByGroupId,
            timeline: parseTimelineMeta(presentation.timeline),
            target: parseTargetMeta(presentation.target, locale),
            resources: parseResourceMeta(presentation.resources),
            session: parseSessionMeta(presentation.session, locale),
            distributions,
            sankey,
            raw: presentation,
        };
    }

    function parseNumericValue(value, path) {
        const record = requireRecord(value, path);
        if (!VALUE_STATES.has(record.state)) {
            throw new ContractError(`${path}.state`, "unsupported numeric state");
        }
        if (record.state === "known") {
            const known = requireExactFields(record, ["state", "unit", "value"], path);
            return {
                state: "known",
                unit: requireString(known.unit, `${path}.unit`),
                value: requireFinite(known.value, `${path}.value`, { minimum: 0 }),
            };
        }
        if (record.state === "unknown") {
            requireExactFields(record, ["state"], path);
            return { state: "unknown" };
        }
        const unavailable = requireExactFields(record, ["reason", "state"], path);
        requireRecord(unavailable.reason, `${path}.reason`);
        return { state: "unavailable", reason: unavailable.reason };
    }

    function parseRate(value, path, { quantified = false } = {}) {
        if (quantified) {
            const rate = requireExactFields(value, ["basis", "value"], path);
            if (!RATE_BASES.has(rate.basis)) {
                throw new ContractError(`${path}.basis`, "unsupported rate basis");
            }
            return {
                state: "known",
                basis: rate.basis,
                value: requireFinite(rate.value, `${path}.value`, { minimum: 0 }),
            };
        }
        const record = requireRecord(value, path);
        if (!VALUE_STATES.has(record.state)) {
            throw new ContractError(`${path}.state`, "unsupported rate state");
        }
        if (record.state === "known") {
            const known = requireExactFields(record, ["basis", "state", "value"], path);
            if (!RATE_BASES.has(known.basis)) {
                throw new ContractError(`${path}.basis`, "unsupported rate basis");
            }
            return {
                state: "known",
                basis: known.basis,
                value: requireFinite(known.value, `${path}.value`, { minimum: 0 }),
            };
        }
        if (record.state === "unknown") {
            requireExactFields(record, ["state"], path);
            return { state: "unknown" };
        }
        const unavailable = requireExactFields(record, ["reason", "state"], path);
        const reasonPath = `${path}.reason`;
        const reason = requireExactFields(
            unavailable.reason,
            ["code", "detailCode", "sourceId"],
            reasonPath,
        );
        return {
            state: "unavailable",
            reason: {
                code: requireString(reason.code, `${reasonPath}.code`),
                detailCode: requireNullableString(
                    reason.detailCode,
                    `${reasonPath}.detailCode`,
                ),
                sourceId: requireNullableString(
                    reason.sourceId,
                    `${reasonPath}.sourceId`,
                ),
            },
        };
    }

    function validateItemQuantity(value, path) {
        const quantity = requireRecord(value, path);
        if (quantity.state === "known") {
            const known = requireExactFields(
                quantity,
                ["state", "minimum", "maximum"],
                path,
            );
            const minimum = requireInteger(known.minimum, `${path}.minimum`, {
                minimum: 0,
                maximum: 4_294_967_295,
            });
            const maximum = requireInteger(known.maximum, `${path}.maximum`, {
                minimum: 0,
                maximum: 4_294_967_295,
            });
            if (maximum < minimum) {
                throw new ContractError(`${path}.maximum`, "must be at least the minimum");
            }
            return quantity;
        }
        if (quantity.state === "unknown") {
            return requireExactFields(quantity, ["state"], path);
        }
        if (quantity.state === "unavailable") {
            requireExactFields(quantity, ["state", "reason"], path);
            parseRate({ state: "unavailable", reason: quantity.reason }, path);
            return quantity;
        }
        throw new ContractError(`${path}.state`, "unsupported item quantity state");
    }

    function validateExpectedItemQuantity(value, path) {
        const quantity = parseNumericValue(value, path);
        if (quantity.state === "known" && quantity.unit !== "count") {
            throw new ContractError(`${path}.unit`, "expected count");
        }
        return quantity;
    }

    const validateLootProjection = createCalculatorLootProjectionValidator({
        fail(path, message) {
            throw new ContractError(path, message);
        },
        requireArray,
        requireExactFields,
        requireItemId(value, path) {
            return requireInteger(value, path, { minimum: 1 });
        },
        requireRecord,
        requireString,
        validateExpectedItemQuantity,
        validateItemQuantity,
        validateRate(value, path) {
            return parseRate(value, path);
        },
        validateStatTermSubject(value, path) {
            return parseStatTermSubject(value, path);
        },
        validateTraceId(value, path) {
            return requireString(value, path);
        },
    });

    function formatKnownNumber(value, format) {
        return `${format.prefix}${format.formatter.format(value * format.multiplier)}${format.suffix}`;
    }

    function formatState(value, formats, presentationValues, path) {
        if (value.state === "unknown") {
            return { state: "unknown", text: presentationValues.unknownLabel };
        }
        if (value.state === "unavailable") {
            return { state: "unavailable", text: presentationValues.unavailableLabel };
        }
        const key = value.unit ?? value.basis;
        const format = formats[key];
        if (!format) {
            throw new ContractError(path, `no presentation format for ${key}`);
        }
        return {
            state: "known",
            text: formatKnownNumber(value.value, format),
        };
    }

    function formatNumeric(value, presentation, path) {
        return formatState(
            value,
            presentation.values.numericUnits,
            presentation.values,
            path,
        );
    }

    function formatTargetDuration(value, presentation, path) {
        if (value.state !== "known") {
            return formatNumeric(value, presentation, path);
        }
        if (value.unit !== "seconds") {
            throw new ContractError(`${path}.unit`, "expected seconds");
        }
        const selected = presentation.target.durationFormats.find(
            (format) => value.value >= format.minimumSeconds,
        );
        if (!selected) {
            throw new ContractError(path, "no target duration format matched");
        }
        const scaledValue = value.value * selected.numberFormat.multiplier;
        const roundedValue = Math.round(scaledValue * 100) / 100;
        const unitLabel = roundedValue === 1
            ? selected.unitLabels.one
            : selected.unitLabels.other;
        return {
            state: "known",
            text: `${formatKnownNumber(value.value, selected.numberFormat)} ${unitLabel}`,
        };
    }

    function formatRate(value, presentation, path) {
        if (
            value.state === "known"
            && (value.basis === "ratio" || value.basis === "percent")
        ) {
            const format = presentation.values.rates[value.basis];
            if (!format) {
                throw new ContractError(path, `no presentation format for ${value.basis}`);
            }
            const maximumFractionDigits = Math.min(2, format.maximumFractionDigits);
            const minimumFractionDigits = Math.min(
                maximumFractionDigits,
                format.minimumFractionDigits,
            );
            return {
                state: "known",
                text: `${format.prefix}${new Intl.NumberFormat(presentation.locale, {
                    minimumFractionDigits,
                    maximumFractionDigits,
                }).format(value.value * format.multiplier)}${format.suffix}`,
            };
        }
        return formatState(value, presentation.values.rates, presentation.values, path);
    }

    function formatLootRateDisplay(value, presentation, path) {
        const format = value?.basis
            ? presentation.values.rates[value.basis]
            : undefined;
        if (value?.state === "known" && !format) {
            throw new ContractError(path, `no loot rate presentation format for ${value.basis}`);
        }
        return calculatorLootRateDisplay(value, {
            locale: presentation.locale,
            numberFormat: format,
            unknownText: presentation.values.unknownLabel,
            unavailableText: presentation.values.unavailableLabel,
        });
    }

    function formatMetricShareDisplay(value, presentation, path) {
        if (value.state === "known" && value.unit !== "ratio") {
            throw new ContractError(`${path}.unit`, "expected ratio");
        }
        return formatLootRateDisplay(
            value.state === "known"
                ? { state: "known", value: value.value, basis: "ratio" }
                : { state: value.state },
            presentation,
            path,
        );
    }

    function formatLootRateDisplayForMode(
        normalizedRate,
        denormalizedRate,
        rawWeight,
        mode,
        presentation,
        path,
    ) {
        if (denormalizedRate?.state === "known" && denormalizedRate.basis !== "percent") {
            throw new ContractError(
                `${path}.denormalizedRate.basis`,
                "known denormalized rate must use percent basis",
            );
        }
        return calculatorLootRateDisplayForMode(
            normalizedRate,
            denormalizedRate,
            rawWeight,
            mode,
            {
                locale: presentation.locale,
                numberFormat: presentation.values.rates.percent,
                unknownText: presentation.values.unknownLabel,
                unavailableText: presentation.values.unavailableLabel,
            },
        );
    }

    function parseSummaryRows(projection) {
        const summaries = requireExactFields(projection.summaries, ["values"], "$.projection.summaries");
        const byMetric = new Map();
        requireArray(summaries.values, "$.projection.summaries.values").forEach((row, index) => {
            const path = `$.projection.summaries.values[${index}]`;
            const value = requireExactFields(
                row,
                ["formulaRows", "metric", "traceIds", "value"],
                path,
            );
            const metric = requireString(value.metric, `${path}.metric`);
            if (byMetric.has(metric)) {
                throw new ContractError(path, `duplicate summary metric ${metric}`);
            }
            requireArray(value.traceIds, `${path}.traceIds`).forEach((traceId, traceIndex) => (
                requireString(traceId, `${path}.traceIds[${traceIndex}]`)
            ));
            byMetric.set(metric, {
                metric,
                value: parseNumericValue(value.value, `${path}.value`),
                formulaRows: formulaPresenter.parseRows(
                    value.formulaRows,
                    `${path}.formulaRows`,
                ),
                traceIds: [...value.traceIds],
            });
        });
        return byMetric;
    }

    function applyTermPattern(pattern, values) {
        return pattern.replace(/\{([a-zA-Z]+)\}/g, (match, key) => (
            Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
        ));
    }

    function appendRelationshipDetail(detail, relationship, statTerms) {
        const relationshipLabel = statTerms.relationships[relationship];
        return detail ? `${detail} · ${relationshipLabel}` : relationshipLabel;
    }

    function resolveStatTermRow(term, stat, presentation, path) {
        const meta = presentation.statTerms;
        const genericLabel = meta.kinds[term.kind];
        if (!genericLabel) {
            throw new ContractError("$.presentation.statTerms.kinds", `missing term ${term.kind}`);
        }
        const formatted = formatNumeric(
            { state: "known", unit: term.unit, value: term.value },
            presentation,
            path,
        );
        const subjects = meta.subjects;
        let label = genericLabel;
        let detail = "";
        let identity = null;
        if (term.subject.kind === "item") {
            identity = presentation.sankeyItemsByItemId[String(term.subject.itemId)] ?? {
                label: applyTermPattern(subjects.itemFallbackPattern, { id: term.subject.itemId }),
                iconUrl: null,
                gradeTone: "unknown",
            };
            label = identity.label;
        } else if (term.subject.kind === "option") {
            identity = subjects.optionsByOptionId[term.subject.optionId] ?? {
                label: term.subject.optionId,
                iconUrl: null,
                gradeTone: "unknown",
                optionKind: null,
            };
            label = identity.label;
            detail = identity.optionKind ? subjects.optionKinds[identity.optionKind] : genericLabel;
        } else if (term.subject.kind === "petOption") {
            const pet = subjects.optionsByOptionId[term.subject.petId] ?? {
                label: term.subject.petId,
                iconUrl: null,
                gradeTone: "unknown",
            };
            const option = subjects.optionsByOptionId[term.subject.optionId];
            identity = pet;
            label = pet.label;
            const optionLabel = option?.label ?? term.subject.optionId;
            const typedOptionLabel = `${optionLabel} (${subjects.petOptionKinds[term.subject.optionKind]})`;
            detail = applyTermPattern(subjects.petDetailPattern, {
                option: typedOptionLabel,
                tier: term.subject.tier,
                slot: term.subject.slot,
                packLeader: term.subject.packLeader ? subjects.packLeaderSuffix : "",
            });
        } else if (term.subject.kind === "fishingSource") {
            detail = subjects.sourceLabel;
        } else if (term.subject.kind === "resources") {
            const percent = formatNumeric(
                { state: "known", unit: "percent", value: term.subject.percent },
                presentation,
                `${path}.subject.percent`,
            ).text;
            detail = applyTermPattern(subjects.resourcesDetailPattern, {
                percent,
                abundance: subjects.abundanceLabels[term.subject.abundance],
            });
        } else if (term.subject.kind === "fishingLevel") {
            detail = applyTermPattern(subjects.fishingLevelDetailPattern, {
                level: term.subject.level,
            });
        } else if (term.subject.kind === "lifeskillLevel") {
            detail = applyTermPattern(subjects.lifeskillLevelDetailPattern, {
                level: term.subject.level,
            });
        } else if (term.subject.kind === "mastery") {
            detail = applyTermPattern(subjects.masteryDetailPattern, {
                mastery: term.subject.mastery,
            });
        } else if (term.subject.kind === "brand") {
            detail = term.subject.active ? subjects.brandActiveLabel : subjects.brandInactiveLabel;
        }
        detail = appendRelationshipDetail(detail, term.relationship, meta);
        const row = {
            kind: identity ? "item" : "text",
            id: `stat:${stat}:term:${term.order}`,
            label,
            valueText: formatted.text,
            detailText: detail,
            emphasis: false,
        };
        if (identity) {
            row.iconUrl = identity.iconUrl;
            row.gradeTone = identity.gradeTone;
        }
        return { role: term.role, order: term.order, row };
    }

    function breakdownSections(id, resultLabel, resultText, resultSectionLabel, termGroups) {
        const sections = [];
        if (termGroups.length) {
            sections.push({
                id: `${id}:inputs`,
                label: termGroups[0].sectionLabel,
                role: "input",
                groups: termGroups.map((group) => ({
                    id: `${id}:terms:${group.role}`,
                    label: group.label,
                    rows: group.rows,
                })),
            });
        }
        sections.push({
            id: `${id}:result`,
            label: resultSectionLabel,
            role: "result",
            groups: [
                {
                    id: `${id}:result-value`,
                    label: "",
                    rows: [
                        {
                            kind: "text",
                            id: `${id}:value`,
                            label: resultLabel,
                            valueText: resultText,
                            detailText: "",
                            emphasis: true,
                        },
                    ],
                },
            ],
        });
        return sections;
    }

    function summaryBreakdown(row, formatted, meta, sourceStat = null) {
        const id = `summary:${row.metric}`;
        const termGroups = sourceStat?.termGroups ?? [];
        return {
            id,
            color: meta.color,
            eyebrow: meta.eyebrow,
            title: meta.title,
            valueText: formatted.text,
            summaryText: "",
            formulaLabel: row.formulaLabel,
            formulaRows: row.presentedFormulaRows,
            sections: breakdownSections(
                id,
                meta.resultLabel,
                formatted.text,
                meta.resultSectionLabel,
                termGroups,
            ),
        };
    }

    function parseStatRows(projection, presentation) {
        const stats = new Map();
        requireArray(projection.statBreakdowns, "$.projection.statBreakdowns")
            .forEach((entry, index) => {
                const path = `$.projection.statBreakdowns[${index}]`;
                const value = requireExactFields(
                    entry,
                    ["formulaRows", "stat", "terms", "traceIds", "value"],
                    path,
                );
                const stat = requireString(value.stat, `${path}.stat`);
                if (stats.has(stat)) {
                    throw new ContractError(path, `duplicate stat ${stat}`);
                }
                const meta = presentation.stats[stat];
                if (!meta) {
                    throw new ContractError("$.presentation.stats", `missing stat ${stat}`);
                }
                const numeric = parseNumericValue(value.value, `${path}.value`);
                const formatted = formatNumeric(numeric, presentation, `${path}.value`);
                const parsedTerms = requireArray(value.terms, `${path}.terms`).map((term, termIndex) => {
                    const termPath = `${path}.terms[${termIndex}]`;
                    const record = requireExactFields(
                        term,
                        [
                            "kind",
                            "order",
                            "relationship",
                            "role",
                            "subject",
                            "traceIds",
                            "unit",
                            "value",
                        ],
                        termPath,
                    );
                    const kind = requireString(record.kind, `${termPath}.kind`);
                    const role = requireString(record.role, `${termPath}.role`);
                    if (!STAT_TERM_ROLE_SET.has(role)) {
                        throw new ContractError(`${termPath}.role`, "unsupported stat term role");
                    }
                    const relationship = requireString(
                        record.relationship,
                        `${termPath}.relationship`,
                    );
                    if (!STAT_TERM_RELATIONSHIP_SET.has(relationship)) {
                        throw new ContractError(
                            `${termPath}.relationship`,
                            "unsupported stat term relationship",
                        );
                    }
                    requireArray(record.traceIds, `${termPath}.traceIds`).forEach(
                        (traceId, traceIndex) => requireString(
                            traceId,
                            `${termPath}.traceIds[${traceIndex}]`,
                        ),
                    );
                    return {
                        kind,
                        role,
                        relationship,
                        order: requireInteger(record.order, `${termPath}.order`),
                        subject: parseStatTermSubject(record.subject, `${termPath}.subject`),
                        unit: requireString(record.unit, `${termPath}.unit`),
                        value: requireFinite(record.value, `${termPath}.value`),
                        traceIds: [...record.traceIds],
                    };
                });
                const termOrders = parsedTerms.map(({ order }) => order).sort((left, right) => left - right);
                if (termOrders.some((order, orderIndex) => order !== orderIndex)) {
                    throw new ContractError(`${path}.terms`, "term orders must be unique and contiguous");
                }
                const presentedTerms = parsedTerms
                    .sort((left, right) => left.order - right.order)
                    .map((term) => resolveStatTermRow(
                        term,
                        stat,
                        presentation,
                        `${path}.terms[${term.order}]`,
                    ));
                const termGroups = ["input", "modifier"].flatMap((role) => {
                    const rows = presentedTerms
                        .filter((term) => term.role === role)
                        .map(({ row }) => row);
                    return rows.length ? [{
                        role,
                        label: presentation.statTerms.roles[role],
                        sectionLabel: meta.inputSectionLabel,
                        rows,
                    }] : [];
                });
                const id = `stat:${stat}`;
                const sections = breakdownSections(
                    id,
                    meta.resultLabel,
                    formatted.text,
                    meta.resultSectionLabel,
                    termGroups,
                );
                stats.set(stat, {
                    state: formatted.state,
                    text: formatted.text,
                    value: numeric,
                    termGroups,
                    termRowsByOrder: new Map(presentedTerms.map(({ order, row }) => [order, {
                        row,
                        value: {
                            state: "known",
                            unit: parsedTerms[order].unit,
                            value: parsedTerms[order].value,
                        },
                    }])),
                    formulaRows: formulaPresenter.parseRows(
                        value.formulaRows,
                        `${path}.formulaRows`,
                    ),
                    breakdown: {
                        id,
                        color: meta.color,
                        eyebrow: meta.eyebrow,
                        title: meta.title,
                        valueText: formatted.text,
                        summaryText: "",
                        formulaLabel: presentation.formulas.label,
                        sections,
                    },
                });
            });
        return stats;
    }

    function orderedByKinds(rows, order, key, path) {
        const rowsByKind = new Map(rows.map((row) => [row[key], row]));
        const ordered = requireArray(order, path).map((kind, index) => {
            const id = requireString(kind, `${path}[${index}]`);
            const row = rowsByKind.get(id);
            if (!row) {
                throw new ContractError(`${path}[${index}]`, `missing ${id}`);
            }
            rowsByKind.delete(id);
            return row;
        });
        if (rowsByKind.size) {
            throw new ContractError(path, `missing order for ${Array.from(rowsByKind.keys())[0]}`);
        }
        return ordered;
    }

    function orderedMap(map, order, path) {
        return new Map(orderedByKinds(
            Array.from(map, ([key, value]) => ({ key, value })),
            order,
            "key",
            path,
        ).map(({ key, value }) => [key, value]));
    }

    function projectTimeline(projection, presentation, summaryPresentations) {
        const timeline = requireExactFields(projection.timeline, ["segments"], "$.projection.timeline");
        const rows = requireArray(timeline.segments, "$.projection.timeline.segments")
            .map((segment, index) => {
                const path = `$.projection.timeline.segments[${index}]`;
                const value = requireExactFields(
                    segment,
                    ["durationSeconds", "kind", "share", "traceIds"],
                    path,
                );
                const kind = requireString(value.kind, `${path}.kind`);
                const meta = presentation.timeline.segments[kind];
                if (!meta) {
                    throw new ContractError("$.presentation.timeline.segments", `missing ${kind}`);
                }
                const duration = parseNumericValue(value.durationSeconds, `${path}.durationSeconds`);
                const share = parseNumericValue(value.share, `${path}.share`);
                const summaryPresentation = summaryPresentations.get(meta.summaryMetric);
                if (!summaryPresentation) {
                    throw new ContractError(
                        "$.presentation.timeline.segments",
                        `missing summary metric ${meta.summaryMetric} for ${kind}`,
                    );
                }
                return {
                    kind,
                    segmentId: kind,
                    weight: share.state === "known" ? share.value : 0,
                    label: meta.label,
                    valueText: formatNumeric(duration, presentation, `${path}.durationSeconds`).text,
                    detailText: formatNumeric(share, presentation, `${path}.share`).text,
                    fillColor: meta.fillColor,
                    strokeColor: meta.strokeColor,
                    breakdown: summaryPresentation.breakdown,
                };
            });
        return {
            segments: orderedByKinds(
                rows,
                projection.presentationOrder.timeline,
                "kind",
                "$.projection.presentationOrder.timeline",
            ).map(({ kind: _kind, ...segment }) => segment),
        };
    }

    function groupRanks(projection) {
        const ranks = new Map();
        requireArray(
            projection.presentationOrder.groups,
            "$.projection.presentationOrder.groups",
        ).forEach((entry, index) => {
            const path = `$.projection.presentationOrder.groups[${index}]`;
            const record = requireExactFields(entry, ["groupId", "rank"], path);
            const groupId = requireString(record.groupId, `${path}.groupId`);
            const rank = requireInteger(record.rank, `${path}.rank`);
            if (ranks.has(groupId) || Array.from(ranks.values()).includes(rank)) {
                throw new ContractError(path, "group keys and ranks must be unique");
            }
            ranks.set(groupId, rank);
        });
        return ranks;
    }

    function sankeyMetricDerivation(formulaContext, kind, groupId, itemId, metric, path) {
        const key = kind === "group"
            ? `${groupId}\u0000${metric}`
            : `${groupId}\u0000${itemId}\u0000${metric}`;
        const derivation = kind === "group"
            ? formulaContext.projectionTargets.groupMetrics.get(key)
            : formulaContext.projectionTargets.itemMetrics.get(key);
        if (!derivation) {
            throw new ContractError(path, `missing ${kind} Sankey metric ${metric}`);
        }
        return derivation;
    }

    function sankeyMetricTitle(presentation, kind, groupId, itemId, metric, path) {
        const group = presentation.sankeyGroupsByGroupId[groupId];
        if (!group) {
            throw new ContractError(`${path}.groupId`, `missing Sankey group ${groupId}`);
        }
        const metricLabel = presentation.formulas.projectionMetricLabels[metric];
        if (kind === "group") {
            return applyTermPattern(presentation.formulas.groupLabelPattern, {
                group: group.label,
                metric: metricLabel,
            });
        }
        const item = presentation.sankeyItemsByItemId[String(itemId)];
        const itemLabel = item?.label ?? applyTermPattern(
            presentation.formulas.unknownItemPattern,
            { id: itemId },
        );
        return applyTermPattern(presentation.formulas.itemGroupLabelPattern, {
            item: itemLabel,
            group: group.label,
            metric: metricLabel,
        });
    }

    function projectSankeyMetricPresentations(projection, presentation, formulaContext) {
        const projectRows = (rows, kind) => new Map(requireArray(
            rows,
            `$.projection.sankey.${kind === "group" ? "groups" : "items"}`,
        ).map((rawRow, index) => {
            const path = `$.projection.sankey.${kind === "group" ? "groups" : "items"}[${index}]`;
            const row = requireRecord(rawRow, path);
            const rowId = requireString(row.rowId, `${path}.rowId`);
            const groupId = requireString(row.groupId, `${path}.groupId`);
            const itemId = kind === "item"
                ? requireInteger(row.itemId, `${path}.itemId`, { minimum: 1 })
                : null;
            const metricPresentations = new Map(CALCULATOR_SANKEY_METRICS.map((metric) => {
                const definition = SANKEY_METRIC_DEFINITIONS[metric];
                const value = parseNumericValue(row[metric], `${path}.${metric}`);
                if (value.state === "known" && value.unit !== definition.unit) {
                    throw new ContractError(
                        `${path}.${metric}.unit`,
                        `expected ${definition.unit}`,
                    );
                }
                const formatted = formatNumeric(value, presentation, `${path}.${metric}`);
                const derivation = sankeyMetricDerivation(
                    formulaContext,
                    kind,
                    groupId,
                    itemId,
                    metric,
                    `${path}.metrics`,
                );
                const owner = kind === "group"
                    ? { kind, groupId, metric }
                    : { kind: "itemGroup", groupId, itemId, metric };
                const formulaRows = formulaPresenter.presentRows(
                    derivation.formulaRows,
                    owner,
                    value,
                    formulaContext,
                    `${derivation.path}.formulaRows`,
                );
                const meta = presentation.summaryMetrics[definition.summaryMetric];
                if (!meta) {
                    throw new ContractError(
                        "$.presentation.summaryMetrics",
                        `missing ${definition.summaryMetric} for Sankey metric ${metric}`,
                    );
                }
                const id = `sankey:${kind}:${rowId}:${metric}`;
                return [metric, Object.freeze({
                    share: derivation.share,
                    breakdown: Object.freeze({
                        id,
                        color: metric === "expectedCount"
                            ? "var(--color-info)"
                            : "var(--color-success)",
                        eyebrow: meta.eyebrow,
                        title: sankeyMetricTitle(
                            presentation,
                            kind,
                            groupId,
                            itemId,
                            metric,
                            path,
                        ),
                        valueText: formatted.text,
                        summaryText: "",
                        formulaLabel: presentation.formulas.label,
                        formulaRows,
                        sections: breakdownSections(
                            id,
                            presentation.formulas.projectionMetricLabels[metric],
                            formatted.text,
                            meta.resultSectionLabel,
                            [],
                        ),
                    }),
                })];
            }));
            return [rowId, metricPresentations];
        }));
        return Object.freeze({
            groups: projectRows(projection.sankey.groups, "group"),
            items: projectRows(projection.sankey.items, "item"),
        });
    }

    function projectDistributions(
        projection,
        presentation,
        formulaContext,
        lootRateDisplayMode,
    ) {
        const groups = requireArray(projection.sankey.groups, "$.projection.sankey.groups");
        const ranks = groupRanks(projection);
        return Object.fromEntries(presentation.distributions.map((definition) => {
            const segments = groups.map((group, index) => {
                const path = `$.projection.sankey.groups[${index}]`;
                const row = requireRecord(group, path);
                const rowId = requireString(row.rowId, `${path}.rowId`);
                const groupId = requireString(row.groupId, `${path}.groupId`);
                const meta = definition.groupsByGroupId[groupId];
                if (!meta) {
                    throw new ContractError(
                        `$.presentation.distributions.${definition.id}.groupsByGroupId`,
                        `missing group ${groupId}`,
                    );
                }
                if (!ranks.has(groupId)) {
                    throw new ContractError("$.projection.presentationOrder.groups", `missing ${groupId}`);
                }
                const metric = parseNumericValue(
                    row[definition.valueField],
                    `${path}.${definition.valueField}`,
                );
                if (metric.state === "known" && metric.unit !== definition.valueUnit) {
                    throw new ContractError(
                        `${path}.${definition.valueField}.unit`,
                        `expected ${definition.valueUnit}`,
                    );
                }
                const formatted = formatNumeric(
                    metric,
                    presentation,
                    `${path}.${definition.valueField}`,
                );
                const derivation = sankeyMetricDerivation(
                    formulaContext,
                    "group",
                    groupId,
                    null,
                    definition.valueField,
                    `${path}.metrics`,
                );
                const formulaRows = derivation.formulaRows;
                const presentedFormulaRows = formulaPresenter.presentRows(
                    formulaRows,
                    { kind: "group", groupId, metric: definition.valueField },
                    metric,
                    formulaContext,
                    `${path}.formulas.${definition.valueField}.rows`,
                );
                const breakdownMeta = presentation.summaryMetrics[definition.summaryMetric];
                if (!breakdownMeta) {
                    throw new ContractError(
                        "$.presentation.summaryMetrics",
                        `missing ${definition.summaryMetric} for ${definition.id}`,
                    );
                }
                let detailRate;
                let detailRateLabel;
                if (definition.id === "group-count") {
                    detailRate = formatLootRateDisplayForMode(
                        parseRate(row.rate, `${path}.rate`, { quantified: true }),
                        parseRate(
                            row.denormalizedRate,
                            `${path}.denormalizedRate`,
                        ),
                        parseRate(row.rawWeight, `${path}.rawWeight`),
                        lootRateDisplayMode,
                        presentation,
                        path,
                    );
                    detailRateLabel = presentation.formulas.projectionMetricLabels[
                        detailRate.basis === "rawWeight"
                            || detailRate.basis === "denormalizedWeight"
                            ? "rawWeight"
                            : "rate"
                    ];
                } else {
                    detailRate = formatMetricShareDisplay(
                        derivation.share,
                        presentation,
                        `${derivation.path}.share`,
                    );
                    detailRateLabel = applyTermPattern(
                        presentation.formulas.groupMetricShareLabelPattern,
                        {
                            group: meta.label,
                            metric: presentation.formulas.projectionMetricLabels[
                                definition.valueField
                            ],
                        },
                    );
                }
                const valueMetricLabel =
                    presentation.formulas.projectionMetricLabels[definition.valueField];
                const breakdownId = `distribution:${definition.id}:${rowId}`;
                return {
                    rank: ranks.get(groupId),
                    rowId,
                    weight: metric.state === "known" ? metric.value : 0,
                    valueState: metric.state,
                    label: meta.label,
                    valueText: formatted.text,
                    valueAriaText: `${valueMetricLabel}: ${formatted.text}`,
                    valueUnitMark: calculatorMetricUnitMarkForValueField(
                        definition.valueField,
                    ),
                    detailRate,
                    detailRateAriaText: `${detailRateLabel}: ${calculatorMetricTextWithUnit(
                        detailRate,
                    )}`,
                    fillColor: meta.fillColor,
                    strokeColor: meta.strokeColor,
                    textColor: meta.textColor,
                    connectorColor: meta.connectorColor,
                    breakdown: {
                        id: breakdownId,
                        color: breakdownMeta.color,
                        eyebrow: breakdownMeta.eyebrow,
                        title: applyTermPattern(presentation.formulas.groupLabelPattern, {
                            group: meta.label,
                            metric: presentation.formulas.projectionMetricLabels[
                                definition.valueField
                            ],
                        }),
                        valueText: formatted.text,
                        summaryText: "",
                        formulaLabel: presentation.formulas.label,
                        formulaRows: presentedFormulaRows,
                        sections: breakdownSections(
                            breakdownId,
                            breakdownMeta.resultLabel,
                            formatted.text,
                            breakdownMeta.resultSectionLabel,
                            [],
                        ),
                    },
                };
            }).sort((left, right) => left.rank - right.rank)
                .map(({ rank: _rank, ...segment }) => segment);
            return [definition.id, { segments }];
        }));
    }

    function applyPattern(pattern, values) {
        return pattern.replace(/\{(count|start|end|timespan|value)\}/g, (match, key) => (
            Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
        ));
    }

    function projectTarget(projection, presentation) {
        const path = "$.projection.target";
        const target = requireExactFields(
            projection.target,
            [
                "amount",
                "distribution",
                "expectedCount",
                "itemId",
                "perDay",
                "pmfTailCount",
                "probabilityAtLeast",
                "timeToTarget",
                "traceIds",
            ],
            path,
        );
        requireInteger(target.amount, `${path}.amount`, { minimum: 1 });
        const expected = parseNumericValue(target.expectedCount, `${path}.expectedCount`);
        const expectedText = formatNumeric(expected, presentation, `${path}.expectedCount`).text;
        const countFormat = presentation.values.numericUnits.count;
        if (!countFormat) {
            throw new ContractError("$.presentation.values.numericUnits", "missing count");
        }
        const countText = (value) => countFormat.formatter.format(value * countFormat.multiplier);
        const bars = requireArray(target.distribution, `${path}.distribution`)
            .map((entry, index) => {
                const bucketPath = `${path}.distribution[${index}]`;
                const record = requireExactFields(entry, ["bucket", "probability"], bucketPath);
                const bucket = requireRecord(record.bucket, `${bucketPath}.bucket`);
                let barId;
                let label;
                let highlight;
                if (bucket.kind === "exact") {
                    requireExactFields(bucket, ["count", "kind"], `${bucketPath}.bucket`);
                    const count = requireInteger(bucket.count, `${bucketPath}.bucket.count`);
                    barId = `exact:${count}`;
                    highlight = count === target.amount;
                    label = applyPattern(presentation.target.bucketLabels.exact, {
                        count: countText(count),
                    });
                } else if (bucket.kind === "inclusiveRange") {
                    requireExactFields(bucket, ["end", "kind", "start"], `${bucketPath}.bucket`);
                    const start = requireInteger(bucket.start, `${bucketPath}.bucket.start`);
                    const end = requireInteger(bucket.end, `${bucketPath}.bucket.end`);
                    if (end < start) {
                        throw new ContractError(`${bucketPath}.bucket`, "range end precedes start");
                    }
                    barId = `inclusiveRange:${start}:${end}`;
                    highlight = target.amount >= start && target.amount <= end;
                    label = applyPattern(presentation.target.bucketLabels.inclusiveRange, {
                        start: countText(start),
                        end: countText(end),
                    });
                } else if (bucket.kind === "atLeast") {
                    requireExactFields(bucket, ["count", "kind"], `${bucketPath}.bucket`);
                    const count = requireInteger(bucket.count, `${bucketPath}.bucket.count`);
                    barId = `atLeast:${count}`;
                    highlight = target.amount >= count;
                    label = applyPattern(presentation.target.bucketLabels.atLeast, {
                        count: countText(count),
                    });
                } else {
                    throw new ContractError(`${bucketPath}.bucket.kind`, "unsupported bucket");
                }
                const probability = parseRate(record.probability, `${bucketPath}.probability`);
                if (
                    probability.state === "known"
                    && probability.basis !== "ratio"
                    && probability.basis !== "percent"
                ) {
                    throw new ContractError(
                        `${bucketPath}.probability.basis`,
                        "expected ratio or percent probability",
                    );
                }
                const formatted = formatRate(probability, presentation, `${bucketPath}.probability`);
                const rateFormat = probability.state === "known"
                    ? presentation.values.rates[probability.basis]
                    : null;
                const probabilityPercent = probability.state === "known"
                    ? probability.value * rateFormat.multiplier
                    : null;
                if (probabilityPercent !== null && (probabilityPercent < 0 || probabilityPercent > 100)) {
                    throw new ContractError(`${bucketPath}.probability`, "PMF probability exceeds 100%");
                }
                return {
                    barId,
                    probabilityPercent,
                    valueState: probability.state,
                    label,
                    valueText: formatted.text,
                    highlight,
                };
            });
        return {
            expectedValueText: applyPattern(
                presentation.target.expectedValuePattern,
                { value: expectedText },
            ),
            bars,
        };
    }

    function parseProjection(value) {
        const projection = requireExactFields(value, PROJECTION_FIELDS, "$.projection");
        if (projection.schemaVersion !== 1) {
            throw new ContractError("$.projection.schemaVersion", "expected exactly 1");
        }
        requireString(projection.engineVersion, "$.projection.engineVersion");
        requireString(projection.modelRevision, "$.projection.modelRevision");
        if (projection.userLayerContentHash !== null) {
            const hash = requireExactFields(
                projection.userLayerContentHash,
                ["algorithm", "digest"],
                "$.projection.userLayerContentHash",
            );
            if (
                (hash.algorithm !== "blake3" && hash.algorithm !== "sha256")
                || !/^[0-9a-f]{64}$/.test(hash.digest)
            ) {
                throw new ContractError(
                    "$.projection.userLayerContentHash",
                    "expected a supported 64-character lowercase content hash",
                );
            }
        }
        requireRecord(projection.normalizedScenario, "$.projection.normalizedScenario");
        validateRateAlternativeResolutions(
            projection.rateAlternativeResolutions,
            "$.projection.rateAlternativeResolutions",
        );
        if (!RESOURCE_ABUNDANCE_STATE_SET.has(projection.resourceAbundance)) {
            throw new ContractError(
                "$.projection.resourceAbundance",
                "unsupported resource abundance state",
            );
        }
        validateLootProjection(
            projection.sankey,
            projection.unquantifiedItems,
            projection.groupRateAuthoring,
            projection.provenanceTraces,
            "$.projection",
        );
        requireArray(projection.provenanceTraces, "$.projection.provenanceTraces");
        requireExactFields(
            projection.presentationOrder,
            ["groups", "items", "stats", "summaries", "timeline"],
            "$.projection.presentationOrder",
        );
        return projection;
    }

    function projectResources(projection, presentation) {
        const resourcesPercent = requireFinite(
            projection.normalizedScenario.resourcesPercent,
            "$.projection.normalizedScenario.resourcesPercent",
            { minimum: 0, maximum: 100 },
        );
        const abundance = projection.resourceAbundance;
        const percent = formatNumeric(
            { state: "known", unit: "percent", value: resourcesPercent },
            presentation,
            "$.projection.normalizedScenario.resourcesPercent",
        ).text;
        return {
            abundance,
            text: presentation.resources.labelPattern.replace(
                /\{(percent|abundance)\}/g,
                (_match, key) => (
                    key === "percent"
                        ? percent
                        : presentation.resources.abundanceLabels[abundance]
                ),
            ),
        };
    }

    function projectSession(projection, presentation) {
        const path = "$.projection.normalizedScenario.session";
        const session = requireRecord(projection.normalizedScenario.session, path);
        const amount = requireFinite(session.timespanAmount, `${path}.timespanAmount`, {
            minimum: 0,
        });
        const unit = requireString(session.timespanUnit, `${path}.timespanUnit`);
        if (!TIMESPAN_UNIT_SET.has(unit)) {
            throw new ContractError(`${path}.timespanUnit`, "unsupported timespan unit");
        }
        const format = presentation.session.timespan.numberFormat;
        const amountText = `${format.prefix}${format.formatter.format(
            amount * format.multiplier,
        )}${format.suffix}`;
        const plurality = Math.abs(amount - 1) < Number.EPSILON ? "one" : "other";
        const timespan = `${amountText} ${
            presentation.session.timespan.unitLabels[unit][plurality]
        }`;
        return {
            timespan,
            ...Object.fromEntries(SESSION_TITLE_PRESENTATIONS.map((name) => [
                name,
                applyPattern(presentation.session.titlePatterns[name], { timespan }),
            ])),
        };
    }

    function project(
        projectionValue,
        parsedPresentation,
        sankeyProjectorCache,
        lootRateDisplayMode,
    ) {
        const projection = parseProjection(projectionValue);
        const parsedStats = orderedMap(
            parseStatRows(projection, parsedPresentation),
            projection.presentationOrder.stats,
            "$.projection.presentationOrder.stats",
        );
        const summaryRows = orderedMap(
            parseSummaryRows(projection),
            projection.presentationOrder.summaries,
            "$.projection.presentationOrder.summaries",
        );
        const formulaContext = {
            presentation: parsedPresentation,
            projectionTargets: formulaPresenter.projectionTargets(projection),
            stats: parsedStats,
            summaryRows,
        };
        const sankeyMetricPresentations = projectSankeyMetricPresentations(
            projection,
            parsedPresentation,
            formulaContext,
        );
        const sankeyProjector = createLootSankeyProjector(
            projection,
            parsedPresentation.sankey,
            sankeyMetricPresentations,
            sankeyProjectorCache,
        );
        for (const [stat, row] of parsedStats) {
            row.breakdown.formulaRows = formulaPresenter.presentRows(
                row.formulaRows,
                { kind: "stat", stat },
                row.value,
                formulaContext,
                `$.projection.statBreakdowns.${stat}.formulaRows`,
            );
        }
        const stats = new Map(Array.from(parsedStats, ([stat, row]) => [stat, {
            state: row.state,
            text: row.text,
            termGroups: row.termGroups,
            breakdown: row.breakdown,
        }]));
        const summaries = new Map();
        for (const [metric, row] of summaryRows) {
            const valuePath = `$.projection.summaries.${metric}.value`;
            const formatted = metric === "targetTime"
                ? formatTargetDuration(row.value, parsedPresentation, valuePath)
                : formatNumeric(row.value, parsedPresentation, valuePath);
            const meta = parsedPresentation.summaryMetrics[metric];
            if (!meta) {
                throw new ContractError(
                    "$.presentation.summaryMetrics",
                    `missing summary metric ${metric}`,
                );
            }
            const sourceStat = meta.statKind === null ? null : stats.get(meta.statKind);
            if (meta.statKind !== null && !sourceStat) {
                throw new ContractError(
                    "$.presentation.summaryMetrics",
                    `missing stat ${meta.statKind} for summary metric ${metric}`,
                );
            }
            row.formulaLabel = parsedPresentation.formulas.label;
            row.presentedFormulaRows = formulaPresenter.presentRows(
                row.formulaRows,
                { kind: "summary", metric },
                row.value,
                formulaContext,
                `$.projection.summaries.${metric}.formulaRows`,
            );
            summaries.set(metric, {
                state: formatted.state,
                text: formatted.text,
                breakdown: summaryBreakdown(row, formatted, meta, sourceStat),
            });
        }
        return {
            resources: projectResources(projection, parsedPresentation),
            session: projectSession(projection, parsedPresentation),
            summaries,
            stats,
            timeline: projectTimeline(projection, parsedPresentation, summaries),
            targetPmf: projectTarget(projection, parsedPresentation),
            distributions: projectDistributions(
                projection,
                parsedPresentation,
                formulaContext,
                lootRateDisplayMode,
            ),
            sankeys: Object.fromEntries(Object.entries(SANKEY_VARIANTS).map(
                ([presenterId, rightMetric]) => [
                    presenterId,
                    sankeyProjector.build(rightMetric, lootRateDisplayMode),
                ],
            )),
        };
    }

    function nodesWithAttribute(root, attribute) {
        return Array.from(root?.querySelectorAll?.(`[${attribute}]`) ?? []);
    }

    function nodesByAttributeValue(root, attribute) {
        const index = new Map();
        for (const node of nodesWithAttribute(root, attribute)) {
            const value = node.getAttribute?.(attribute);
            if (typeof value !== "string") {
                continue;
            }
            const nodes = index.get(value) ?? [];
            nodes.push(node);
            index.set(value, nodes);
        }
        return index;
    }

    function namedNodes(index, name) {
        return index.get(name) ?? [];
    }

    function setPresenterContentState(presenters, emptyStates, name, hasContent) {
        for (const node of namedNodes(presenters, name)) {
            node.hidden = !hasContent;
        }
        for (const node of namedNodes(emptyStates, name)) {
            node.hidden = hasContent;
        }
    }

    function createViewBindings(root) {
        return Object.freeze({
            presenters: nodesByAttributeValue(root, "data-calculator-presenter"),
            emptyStates: nodesByAttributeValue(root, "data-calculator-presenter-empty"),
            scenarioPresentations: nodesByAttributeValue(
                root,
                "data-calculator-scenario-presentation",
            ),
            summaryMetrics: nodesWithAttribute(root, "data-calculator-summary-metric"),
            summaryBreakdowns: nodesWithAttribute(
                root,
                "data-calculator-summary-breakdown",
            ),
            statKinds: nodesWithAttribute(root, "data-calculator-stat-kind"),
            statValues: nodesWithAttribute(root, "data-calculator-stat-value"),
        });
    }

    function applyView(bindings, view) {
        const { presenters, emptyStates } = bindings;
        for (const node of namedNodes(bindings.scenarioPresentations, "resources")) {
            node.textContent = view.resources.text;
            node.setAttribute?.("data-calculator-resource-abundance", view.resources.abundance);
        }
        for (const [name, text] of Object.entries(view.session)) {
            for (const node of namedNodes(bindings.scenarioPresentations, name)) {
                node.textContent = text;
            }
        }
        for (const node of bindings.summaryMetrics) {
            const metric = node.getAttribute?.("data-calculator-summary-metric");
            const summary = view.summaries.get(metric);
            if (!summary) {
                continue;
            }
            node.textContent = summary.text;
            node.setAttribute?.("data-calculator-value-state", summary.state);
        }
        for (const node of bindings.summaryBreakdowns) {
            const metric = node.getAttribute?.("data-calculator-summary-breakdown");
            node[STAT_PRESENTATION_PROPERTY] = view.summaries.get(metric)?.breakdown ?? null;
        }
        for (const node of bindings.statKinds) {
            const stat = node.getAttribute?.("data-calculator-stat-kind");
            node[STAT_PRESENTATION_PROPERTY] = view.stats.get(stat)?.breakdown ?? null;
        }
        for (const node of bindings.statValues) {
            const stat = node.getAttribute?.("data-calculator-stat-value");
            const value = view.stats.get(stat);
            if (!value) {
                continue;
            }
            node.textContent = value.text;
            node.setAttribute?.("data-calculator-value-state", value.state);
        }
        for (const node of namedNodes(presenters, "timeline")) {
            node.presentation = view.timeline;
        }
        setPresenterContentState(
            presenters,
            emptyStates,
            "timeline",
            view.timeline.segments.length > 0,
        );
        for (const node of namedNodes(presenters, "target-pmf")) {
            node.presentation = view.targetPmf;
        }
        setPresenterContentState(
            presenters,
            emptyStates,
            "target-pmf",
            view.targetPmf.bars.length > 0,
        );
        for (const [id, presentation] of Object.entries(view.distributions)) {
            const presenterId = `distribution:${id}`;
            for (const node of namedNodes(presenters, presenterId)) {
                node.presentation = presentation;
            }
            setPresenterContentState(
                presenters,
                emptyStates,
                presenterId,
                presentation.segments.some(({ weight }) => weight > 0),
            );
        }
        for (const [presenterId, rows] of Object.entries(view.sankeys)) {
            for (const node of namedNodes(presenters, presenterId)) {
                node.rows = rows;
            }
            const hasContent = rows.groups.length > 0
                && rows.items.length > 0;
            setPresenterContentState(
                presenters,
                emptyStates,
                presenterId,
                hasContent,
            );
        }
    }

    function create({ document, locale, root = document, staticPresentation }) {
        if (!document || typeof document.querySelectorAll !== "function") {
            throw new TypeError("document must provide querySelectorAll");
        }
        if (!root || typeof root.querySelectorAll !== "function") {
            throw new TypeError("root must provide querySelectorAll");
        }
        const normalizedLocale = requireString(locale, "$.locale");
        try {
            new Intl.NumberFormat(normalizedLocale);
        } catch (_error) {
            throw new ContractError("$.locale", "invalid locale");
        }
        const parsedPresentation = parseStaticPresentation(
            staticPresentation,
            normalizedLocale,
        );
        const bindings = createViewBindings(root);
        const sankeyProjectorCache = createLootSankeyProjectorCache();
        if (
            requireString(
                parsedPresentation.sankey.locale,
                "$.presentation.sankey.locale",
            ) !== normalizedLocale
        ) {
            throw new ContractError(
                "$.presentation.sankey.locale",
                "must match the calculator presentation locale",
            );
        }
        return Object.freeze({
            present(projection, options = {}) {
                const record = requireRecord(options, "$.options");
                const fields = Object.keys(record);
                if (
                    fields.length > 1
                    || (fields.length === 1 && fields[0] !== "lootRateDisplayMode")
                ) {
                    throw new ContractError(
                        "$.options",
                        "fields may only include: lootRateDisplayMode",
                    );
                }
                const lootRateDisplayMode = parseCalculatorLootRateDisplayMode(
                    record.lootRateDisplayMode ?? "normalized",
                    "$.options.lootRateDisplayMode",
                );
                const view = project(
                    projection,
                    parsedPresentation,
                    sankeyProjectorCache,
                    lootRateDisplayMode,
                );
                applyView(bindings, view);
                return view;
            },
        });
    }

    global[API_NAME] = Object.freeze({ create, ContractError });
    if (typeof global.dispatchEvent === "function" && typeof global.CustomEvent === "function") {
        global.dispatchEvent(new global.CustomEvent(
            "fishystuff:calculator-projection-presenter-ready",
        ));
    }
})(globalThis);
